import { LEAGUES } from '@/models/seasons';
import type { SeasonManager } from '@/systems/SeasonManager';

export class SeasonPanel {
  private container: HTMLElement;
  private seasonNameEl: HTMLElement;
  private seasonTimeEl: HTMLElement;
  private leagueIconEl: HTMLElement;
  private leagueNameEl: HTMLElement;
  private rankEl: HTMLElement;
  private scoreEl: HTMLElement;
  private leagueRows: Map<string, HTMLElement> = new Map();

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.id = 'season-panel';
    this.container.className = 'game-panel';
    this.container.style.cssText = 'display: none; width: 300px;';

    // Header
    const header = document.createElement('div');
    header.className = 'game-panel-header';
    header.innerHTML = `
      <div class="game-panel-title" style="color: var(--gold);">🏅 SEASON</div>
      <button class="game-panel-close">&times;</button>
    `;
    header.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    this.container.appendChild(header);

    // Season name
    this.seasonNameEl = document.createElement('div');
    this.seasonNameEl.style.cssText = 'text-align: center; font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 4px;';
    this.seasonNameEl.textContent = '—';
    this.container.appendChild(this.seasonNameEl);

    // Season time remaining
    this.seasonTimeEl = document.createElement('div');
    this.seasonTimeEl.className = 'season-time';
    this.seasonTimeEl.textContent = 'No active season';
    this.container.appendChild(this.seasonTimeEl);

    // Current league display
    const leagueDisplay = document.createElement('div');
    leagueDisplay.style.cssText = 'display: flex; align-items: center; justify-content: center; gap: 10px; margin: 12px 0 6px;';

    this.leagueIconEl = document.createElement('div');
    this.leagueIconEl.style.cssText = 'font-size: 32px;';
    this.leagueIconEl.textContent = '🥉';

    const leagueInfo = document.createElement('div');

    this.leagueNameEl = document.createElement('div');
    this.leagueNameEl.style.cssText = 'font-size: 18px; font-weight: 700; color: var(--gold);';
    this.leagueNameEl.textContent = 'Bronze';

    leagueInfo.appendChild(this.leagueNameEl);
    leagueDisplay.appendChild(this.leagueIconEl);
    leagueDisplay.appendChild(leagueInfo);
    this.container.appendChild(leagueDisplay);

    // Rank
    this.rankEl = document.createElement('div');
    this.rankEl.className = 'season-rank';
    this.rankEl.style.color = 'var(--gold)';
    this.rankEl.textContent = '#— / —';
    this.container.appendChild(this.rankEl);

    // Score
    this.scoreEl = document.createElement('div');
    this.scoreEl.style.cssText = 'text-align: center; font-size: 11px; color: var(--text-dim); margin-bottom: 12px;';
    this.scoreEl.textContent = 'Score: 0';
    this.container.appendChild(this.scoreEl);

    // League list
    const listWrapper = document.createElement('div');
    listWrapper.style.cssText = 'max-height: 40vh; overflow-y: auto;';

    for (const league of LEAGUES) {
      const row = document.createElement('div');
      row.className = 'season-league-row';

      const icon = document.createElement('span');
      icon.className = 'season-league-icon';
      icon.textContent = league.icon;

      const name = document.createElement('span');
      name.className = 'season-league-name';
      name.style.color = league.color;
      name.textContent = league.name;

      const range = document.createElement('span');
      range.style.cssText = 'font-size: 10px; color: var(--text-dim);';
      range.textContent = `Top ${league.percentileMin}–${league.percentileMax}%`;

      const reward = document.createElement('span');
      reward.className = 'season-league-reward';
      reward.textContent = `💎 ${league.gemsReward}`;

      row.appendChild(icon);
      row.appendChild(name);
      row.appendChild(range);
      row.appendChild(reward);

      this.leagueRows.set(league.id, row);
      listWrapper.appendChild(row);
    }

    this.container.appendChild(listWrapper);
    parent.appendChild(this.container);
  }

  update(manager: SeasonManager): void {
    const season = manager.getCurrentSeason();
    const leagueDef = manager.getLeagueDef();
    const rank = manager.getPlayerRank();
    const total = manager.getTotalPlayers();
    const score = manager.getPlayerScore();
    const playerLeagueId = manager.getPlayerLeague();

    // Season info
    this.seasonNameEl.textContent = season ? season.name : 'No active season';
    this.seasonTimeEl.textContent = manager.getSeasonTimeRemaining();

    // Current league
    this.leagueIconEl.textContent = leagueDef.icon;
    this.leagueNameEl.textContent = leagueDef.name;
    this.leagueNameEl.style.color = leagueDef.color;

    // Rank + score
    this.rankEl.textContent = total > 0 ? `#${rank} / ${total}` : '#—';
    this.scoreEl.textContent = `Score: ${Math.floor(score).toLocaleString()}`;

    // Highlight current league row
    for (const [id, row] of this.leagueRows) {
      row.classList.toggle('current', id === playerLeagueId);
    }
  }

  show(): void { this.container.style.display = 'block'; }
  hide(): void { this.container.style.display = 'none'; }
  toggle(): void {
    this.container.style.display === 'none' ? this.show() : this.hide();
  }
  destroy(): void { this.container.remove(); }
}
